
import { NgOptimizedImage } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormatDatePipe } from '../../pipes/format-date.pipe';
import { NumberCommaPipe } from '../../pipes/number-comma.pipe';
import { TwDatePipe } from '../../pipes/tw-date.pipe';
import { ModifyExamClothesDialogComponent } from '../../shared/components/dialogs/modify-exam-clothes-dialog/modify-exam-clothes-dialog.component';
import { ModifyExamDietDialogComponent } from '../../shared/components/dialogs/modify-exam-diet-dialog/modify-exam-diet-dialog.component';
import { ModifyExamReportDialogComponent } from '../../shared/components/dialogs/modify-exam-report-dialog/modify-exam-report-dialog.component';
import { SingleActionDialogComponent } from '../../shared/components/dialogs/single-action-dialog/single-action-dialog.component';
import { API_PARAMS } from '../../shared/constants/api.constants';
import {
  FULL_DAY_CHECKUP_HOURS,
  ID_LOGIN_TYPE,
  IMAGE_DEPT_NAME,
} from '../../shared/constants/common.constants';
import {
  BusinessConfirmAppointmentRequest,
  ConfirmAppointmentRequest,
} from '../../shared/interfaces/appointment-confirm.model';
import {
  AppointmentInfoField,
  BookingInfo,
  BusinessCheckupSettingsRequest,
  PersonalCheckupSettingsRequest,
} from '../../shared/interfaces/appointment-info.model';
import { ModifyExamClothesDialogModel } from '../../shared/interfaces/dialogs/modify-exam-clothes-dialog.model';
import { ModifyExamDietDialogModel } from '../../shared/interfaces/dialogs/modify-exam-diet-dialog.model';
import { ModifyExamReportDialogModel } from '../../shared/interfaces/dialogs/modify-exam-report-dialog.model';
import { AddExamInfo } from '../../shared/interfaces/reserve-add.model';
import { ExamItemName, Question } from '../../shared/interfaces/reserve-confirm-survey.model';
import { UserPlanRequest } from '../../shared/interfaces/reserve-health.model';
import {
  BusinessExamInfo,
  BusinessPackageInfo,
  ExamInfo,
  Group,
  GroupInfo,
  PackageDetail,
} from '../../shared/interfaces/reserve-loading.model';
import { OrgDetail } from '../../shared/interfaces/reserve-location.model';
import { AppointmentConfirmService } from '../../shared/services/appointment-confirm.service';
import { AppointmentInfoService } from '../../shared/services/appointment-info.service';
import { ReserveLocationService } from '../../shared/services/reserve-location.service';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { CreateMemberRequest } from '../../shared/interfaces/set-password.model';
import { STORAGE_KEY } from '../../shared/constants/store-key.constants';
import { Decimal } from 'decimal.js';
import { ToastrService } from 'ngx-toastr';
import { DialogHelperService } from '../../shared/services/dialog-helper.service';
import { THREE_BAO_ITEMS } from '../../shared/constants/threee-bao.constants';

/**
 * 確認預約資料組件
 */
@Component({
  selector: 'app-appointment-confirm',
  standalone: true,
  templateUrl: './appointment-confirm.component.html',
  imports: [
    TranslatePipe,
    MatButton,
    NumberCommaPipe,
    NgOptimizedImage,
    MatIconButton,
  ],
  styleUrl: './appointment-confirm.component.scss',
})
export class AppointmentConfirmComponent implements OnInit {
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  translateService: TranslateService = inject(TranslateService);
  reserveLocationService: ReserveLocationService = inject(
    ReserveLocationService
  );
  appointmentConfirmService: AppointmentConfirmService = inject(
    AppointmentConfirmService
  );
  appointmentInfoService: AppointmentInfoService = inject(
    AppointmentInfoService
  );
  router: Router = inject(Router);
  dialog: MatDialog = inject(MatDialog);
  toastr: ToastrService = inject(ToastrService);
  private dialogHelperService = inject(DialogHelperService);

  twDatePipe: TwDatePipe = new TwDatePipe();
  formatDatePipe: FormatDatePipe = new FormatDatePipe();

  showFullExamInfos: boolean = false; // 是否顯示全部檢項
  invalidExamItemQuestionMap: Map<string, Array<Question>> | undefined; // 無法施作的檢項及其未通過的問題 { examItem: string, question: Array<Question> }
  isShowNutrition: boolean = true; // 是否顯示營養諮詢

  userPlanRequest: UserPlanRequest | undefined = undefined;
  bookingInfo: BookingInfo | undefined = undefined;
  loginStatus: string = '';
  uuid: string = ''; //快速登入時
  recipient: string = ''; //體檢報告收件人
  recipientCell: string = ''; //體檢報告收件人手機號碼
  isShowRecipientCell: boolean = false; // 是否顯示收件人手機號碼

  selectedPackage: string = ''; // 選擇的健檢方案
  examInfoListByType: Map<string, Map<string, Array<ExamInfo>>> = new Map(); // 方案底下的所有檢項(按照examType分類)
  addExamItemList: Array<AddExamInfo> = []; // 加選項目列表

  // businessGroupUnderPackageByDept: Map<string, Array<Group>> = new Map(); // 企業方案底下的examInfo(按照deptName分類)
  // businessExamInfoUnderPackageByDept: Map<string, Array<BusinessExamInfo>> =
  //   new Map(); // 企業方案底下的group(按照deptName分類)

  planRemoveExamList: Array<string> = []; // 方案移除的檢項清單

  // 三寶項目
  threeBaoItems: Array<ExamInfo> = THREE_BAO_ITEMS;

  orgNo: string = ''; // 健檢中心
  orgDetails: Array<OrgDetail> = []; // 健檢中心列表

  // deptName是否展開 { deptName: isOpen }
  isDeptNameOpen: Map<string, boolean> = new Map();

  breakfasts: Array<AppointmentInfoField> = []; // 早餐選項
  lunches: Array<AppointmentInfoField> = []; // 午餐選項
  clothesSizes: Array<AppointmentInfoField> = []; // 健檢服尺寸
  languages: Array<AppointmentInfoField> = []; // 檢查報告語言

  breakfast: string = '';
  lunch: string = '';
  examReportLanguage: string = ''; // 檢查報告語言
  examClothesSize: string = ''; // 健檢服尺寸

  // 活動折扣率
  partyDiscountRate: string = '';

  // 是否已發送預約
  isAppointmentSent: boolean = false;

  // examType底下deptName是否展開
  isDeptNameOpenByExamType: Map<string, Map<string, boolean>> = new Map(); // { examType: { deptName: isOpen } }

  createMemberRequest = this.sharedStorageService.get(STORAGE_KEY.CREATE_MEMBER_REQUEST) as CreateMemberRequest | undefined;

  ngOnInit(): void {
    // 設置當前預約步驟為8
    setTimeout(() => {
      this.sharedStorageService.set('reserveStep', 8);
    });

    const partyDiscountRate = this.sharedStorageService.get('partyDiscountRate') ?? '';
    if (partyDiscountRate) {
      this.partyDiscountRate = partyDiscountRate;
    }

    // 初始化無法施作的檢項 - 處理 Map 轉換
    const invalidExamData = this.sharedStorageService.get('invalidExamQuestionMap');
    if (invalidExamData) {
      this.invalidExamItemQuestionMap = new Map<string, Array<Question>>();

      // 如果是普通物件，轉換為 Map
      if (typeof invalidExamData === 'object' && !Array.isArray(invalidExamData)) {
        Object.entries(invalidExamData).forEach(([key, value]) => {
          this.invalidExamItemQuestionMap!.set(key, value as Array<Question>);
        });
      }
    }

    // 個人預約請求資訊
    this.userPlanRequest = this.sharedStorageService.get('userPlanRequest');
    // 企業預約請求資訊
    const businessPlanRequest = this.sharedStorageService.get('businessPlanRequest');
    // 預約資訊
    this.bookingInfo = this.sharedStorageService.get('bookingInfo');
    // 登入身分(已是會員/第一次登入)
    this.loginStatus = this.sharedStorageService.get('loginStatus') ?? '';

    //收件人資料是否同本人
    const deliveryRecipient = API_PARAMS.DELIVERY_METHOD.SELF.code;//因應需求改成本人
    if (deliveryRecipient == API_PARAMS.DELIVERY_METHOD.SELF.code) {
      this.recipient = this.userPlanRequest?.name || this.createMemberRequest?.name || '';
      this.recipientCell = this.userPlanRequest?.cell || '0';
      this.isShowRecipientCell = false;
    } else {
      //非本人
      this.recipient = this.bookingInfo?.recipient ?? '';
      this.recipientCell = this.bookingInfo?.recipientCell ?? '';
      this.isShowRecipientCell = true;//若非本人則顯示收件人電話
    }
    console.log("this.recipient:", this.recipient, "this.recipientCell:", this.recipientCell);

    if (this.isPersonalReserve) {
      // 個人預約
      // 取得選擇之方案
      this.selectedPackage =
        this.sharedStorageService.get('selectedPackage') ?? '';

      // 取得方案檢項並過濾掉無法施作的檢項
      const originalExamItemList =
        this.sharedStorageService.get('examItemList')?.filter(item => {
          return item.selectFlag;
        }) ?? [];
      const filteredExamItemList =
        this.filterInvalidItems(originalExamItemList);

      this.examInfoListByType = filteredExamItemList.reduce(
        (typeMap, examInfo) => {
          const type = examInfo.examType ?? 'unknown';
          const dept = examInfo.deptName ?? 'unknown';
          if (!typeMap.has(type)) {
            typeMap.set(type, new Map<string, ExamInfo[]>());
          }
          const deptMap = typeMap.get(type)!;
          if (!deptMap.has(dept)) {
            deptMap.set(dept, []);
          }
          deptMap.get(dept)!.push(examInfo);
          return typeMap;
        },
        new Map<string, Map<string, ExamInfo[]>>()
      );

      // 初始化dept是否展開
      for (let deptMap of this.examInfoListByType.values()) {
        for (let deptKey of deptMap.keys()) {
          this.isDeptNameOpen.set(deptKey, false); // 初始化deptName為未展開
        }
      }

      console.log("this.isContactAddress:", this.isContactAddress);
      console.log("this.recipient:", this.recipient, "this.recipientCell:", this.recipientCell);

    } else {
      // 企業預約
      const businessSelectedPackage = this.sharedStorageService.get(
        'businessSelectedPackage'
      );

      if (businessSelectedPackage) {
        // 取得企業預約選擇之方案
        this.selectedPackage = businessSelectedPackage.packageName;

        // 初始化dept是否展開
        for (let deptName of businessSelectedPackage?.deptList.map(
          (businessPackageInfo: BusinessPackageInfo) => businessPackageInfo.deptName
        ) ?? []) {
          this.isDeptNameOpen.set(deptName, false);
        }
      }
    }

    // 取得加選項目列表
    this.addExamItemList =
      this.sharedStorageService.get('addExamItemList')
        ?.filter((addExamInfo: AddExamInfo) => (addExamInfo.selectFlag ?? 1) === 1) ?? [];

    console.log("this.addExamItemList:", this.addExamItemList);

    // 取得加選項目列表並過濾掉無法施作的檢項
    const originalAddExamItemList =
      this.sharedStorageService.get('addExamItemList')
        ?.filter((addExamInfo: AddExamInfo) => (addExamInfo.selectFlag ?? 1) === 1) ?? [];
    this.addExamItemList = this.filterInvalidItems(originalAddExamItemList);

    // 取得健檢中心
    this.orgNo = this.userPlanRequest?.orgNo ?? businessPlanRequest?.orgNo ?? '';

    if (this.isPersonalReserve) {
      // 個人預約
      console.log("個人預約");
      const requestType = {
        reservationType: this.sharedStorageService.get('reserveType') ?? '',
      };
      console.log('requestType:', requestType);
      this.reserveLocationService.checkLocations(requestType).subscribe(res => {
        if (res.success && res.data?.orgDetail) {
          this.orgDetails = res.data.orgDetail;
        } else {
          // EC2004 錯誤狀況，直接設為空陣列
          this.orgDetails = [];
          console.warn('查詢據點失敗:', res.statusCode, res.errorMessage);
        }
      });
    } else {
      // 企業預約
      console.log("企業預約");
      const requestType = {
        reservationType: this.sharedStorageService.get('reserveType') ?? '',
        companyNo: businessPlanRequest?.companyNo ?? '',
      };
      console.log('requestType:', requestType);
      this.reserveLocationService.checkLocations(requestType).subscribe(res => {
        if (res.success && res.data?.orgDetail) {
          this.orgDetails = res.data.orgDetail;
        } else {
          // EC2004 錯誤狀況，直接設為空陣列
          this.orgDetails = [];
          console.warn('查詢據點失敗:', res.statusCode, res.errorMessage);
        }
      });
    }

    // 取得早餐午餐選項
    if (this.isPersonalReserve) {
      console.log("個人預約");
      // 取得預約檢項
      const sharedStorageExamItemList =
        this.sharedStorageService.get('examItemList') ?? [];
      const newExamItemList = sharedStorageExamItemList.map(item => ({
        examItem: item.examItem,
        examName: item.examName,
        selectFlag: item.selectFlag ?? 0,
      }));
      // 取得加選檢項
      const addList: Array<ExamItemName> =
        this.sharedStorageService
          .get('addExamItemList')
          ?.filter((addExamInfo: AddExamInfo) => (addExamInfo.selectFlag ?? 0) === 1)
          ?.map((addExamInfo: AddExamInfo) => {
            return {
              examItem: addExamInfo.examItem,
              examName: addExamInfo.examName,
            };
          }) ?? [];
      const request: PersonalCheckupSettingsRequest = {
        dataType: API_PARAMS.CHECKUP_SETTINGS_DATATYPE.ALL,
        caseNo: this.sharedStorageService.get('myReservation')?.caseNo ?? this.sharedStorageService.get('caseNo') ?? '',
        companyNo: '1150',
        orgNo: this.sharedStorageService.get('userPlanRequest')?.orgNo ?? '',
        gender: this.sharedStorageService.get('userPlanRequest')?.gender ?? '',
        idNo: this.sharedStorageService.get('userPlanRequest')?.idNo ?? '',
        UUID: this.sharedStorageService.get('userPlanRequest')?.uuid ?? '',
        bookDate: this.sharedStorageService.get('myReservation')?.bookDate ?? '',
        examItemList: newExamItemList,
        addList: addList,
      };

      this.appointmentInfoService.checkupSettings(request).subscribe(res => {
        this.breakfasts = res.data?.breakfastInfos ?? [];
        this.lunches = res.data?.lunchInfos ?? [];
        this.languages = res.data?.languInfos ?? [];
        this.clothesSizes = res.data?.sizeInfos ?? [];
        // 抓 API 餐食(從代號找中文)
        const breakfastUno = this.bookingInfo?.breakfast;
        this.breakfast =
          this.breakfasts.find(item => item.uno === breakfastUno)?.noDesc ?? '';
        const lunchUno = this.bookingInfo?.lunch;
        this.lunch =
          this.lunches.find(item => item.uno === lunchUno)?.noDesc ?? '';
        // 抓 API 語言(從代號找中文)
        const languageUno = this.bookingInfo?.language;
        this.examReportLanguage =
          this.languages.find(item => item.uno === languageUno)?.noDesc ?? '';
        // 抓 API 健檢服尺寸(從代號找中文)
        const clothesSizeUno = this.bookingInfo?.clothesSize;
        this.examClothesSize =
          this.clothesSizes.find(item => item.uno === clothesSizeUno)?.noDesc ??
          '';
      });
    } else {
      console.log("企業預約");
      // 取得預約檢項    
      const sharedStorageExamItemList =
        this.sharedStorageService.get('examItemList') ?? [];
      const newExamItemList = sharedStorageExamItemList.map(item => ({
        examItem: item.examItem,
        examName: item.examName
      }));
      // 取得加選檢項
      const sharedStorageAddList =
        this.sharedStorageService.get('addExamItemList') ?? [];
      const addList = sharedStorageAddList
        ?.filter((addExamInfo: AddExamInfo) => (addExamInfo.selectFlag ?? 0) === 1)
        .map(item => ({
          examItem: item.examItem,
          examName: item.examName
        }));
      const businessPlanRequest = this.sharedStorageService.get('businessPlanRequest');
      const idLoginRequest = this.sharedStorageService.get('idLoginRequest');//走漸進式登入
      const businessLoginInfo = this.sharedStorageService.get('businessLoginInfo');//走會員登入
      const createMemberRequest
        = this.sharedStorageService.get('createMemberRequest');//走會員登入
      // 查詢檢查相關資料欄位CB-074
      const request: BusinessCheckupSettingsRequest = {
        dataType: API_PARAMS.CHECKUP_SETTINGS_DATATYPE.ALL,
        caseNo: this.sharedStorageService.get('myReservation')?.caseNo ?? this.sharedStorageService.get('caseNo') ?? '',
        companyNo: businessPlanRequest?.companyNo ?? '',
        familyFlag: idLoginRequest?.identity ?? businessLoginInfo?.identity ?? this.sharedStorageService.get('myReservation')?.familyFlag ?? '',
        orgNo: businessPlanRequest?.orgNo ?? '',
        gender: businessPlanRequest?.gender ?? '',
        idNo: idLoginRequest?.idNo ?? createMemberRequest?.idNo ?? '',
        bookDate: this.sharedStorageService.get('myReservation')?.bookDate ?? '',
        examItemList: newExamItemList,
        addList: addList,
      };
      console.log('企業檢查request:', request);
      // CB-074
      this.appointmentInfoService
        .businessCheckupSettings(request)
        .subscribe(res => {
          this.breakfasts = res.data?.breakfastInfos ?? [];
          this.lunches = res.data?.lunchInfos ?? [];
          this.languages = res.data?.languInfos ?? [];
          this.clothesSizes = res.data?.sizeInfos ?? [];
          // 抓 API 餐食(從代號找中文)
          const breakfastUno = this.bookingInfo?.breakfast;
          this.breakfast =
            this.breakfasts.find(item => item.uno === breakfastUno)?.noDesc ?? '';
          const lunchUno = this.bookingInfo?.lunch;
          this.lunch =
            this.lunches.find(item => item.uno === lunchUno)?.noDesc ?? '';
          // 抓 API 語言(從代號找中文)
          const languageUno = this.bookingInfo?.language;
          this.examReportLanguage =
            this.languages.find(item => item.uno === languageUno)?.noDesc ?? '';
          // 抓 API 健檢服尺寸(從代號找中文)
          const clothesSizeUno = this.bookingInfo?.clothesSize;
          this.examClothesSize =
            this.clothesSizes.find(item => item.uno === clothesSizeUno)?.noDesc ??
            '';
        });
    }

  }

  // 統一的過濾方法 - 支援多種檢項類型
  private filterInvalidItems<
    T extends { examItem?: string; groupItem?: string },
  >(itemList: Array<T>): Array<T> {
    if (
      !this.invalidExamItemQuestionMap ||
      this.invalidExamItemQuestionMap.size === 0
    ) {
      return itemList;
    }

    return itemList.filter(item => {
      // 檢查檢項是否在無法施作清單中
      // 優先檢查 examItem，如果不存在則檢查 groupItem
      const itemKey = item.examItem || item.groupItem;
      return itemKey ? !this.invalidExamItemQuestionMap!.has(itemKey) : true;
    });
  }

  // // 新增過濾無法施作檢項的方法
  // private filterInvalidExamItems(examItemList: Array<ExamInfo>): Array<ExamInfo> {
  //   if (!this.invalidExamItemQuestionMap || this.invalidExamItemQuestionMap.size === 0) {
  //     return examItemList
  //   }
  //
  //   return examItemList.filter(examInfo => {
  //     // 檢查檢項是否在無法施作清單中
  //     return !this.invalidExamItemQuestionMap!.has(examInfo.examItem)
  //   })
  // }
  //
  // // 新增過濾無法施作加選項目的方法
  // private filterInvalidAddExamItems(addExamItemList: Array<AddExamInfo>): Array<AddExamInfo> {
  //   if (!this.invalidExamItemQuestionMap || this.invalidExamItemQuestionMap.size === 0) {
  //     return addExamItemList
  //   }
  //
  //   return addExamItemList.filter(addExamInfo => {
  //     // 檢查加選項目是否在無法施作清單中
  //     return !this.invalidExamItemQuestionMap!.has(addExamInfo.examItem)
  //   })
  // }

  // 切換顯示全部檢項
  toggleShowFullExamInfos(): void {
    this.showFullExamInfos = !this.showFullExamInfos;
  }

  // 是否為個人預約
  get isPersonalReserve(): boolean {
    return (
      this.sharedStorageService.get('reserveType') === ID_LOGIN_TYPE.PERSONAL
    );
  }

  // 取得企業選擇的方案
  get businessSelectedPackage(): PackageDetail | undefined {
    return this.sharedStorageService.get('businessSelectedPackage');
  }

  // 企業方案deptName列表
  get businessDeptNames(): Array<string> {
    return (
      this.businessSelectedPackage?.deptList.map(
        (businessPackageInfo: BusinessPackageInfo) => businessPackageInfo.deptName
      ) ?? []
    );
  }

  // 應付金額
  get paymentAmount(): number {
    // 個人預約
    if (this.sharedStorageService.get('reserveType') === ID_LOGIN_TYPE.PERSONAL) {
      return Number(this.sharedStorageService.get('paymentAmount')) ?? 0;
      // 企業預約
    } else {
      return Number(this.sharedStorageService.get('businessSelectedPackage')?.price) ?? 0;
    }
  }

  // 選擇之方案金額
  get packagePrice(): number {
    if (this.isPersonalReserve) {
      return Number(this.sharedStorageService.get('packagePrice')) ?? 0;
    } else {
      return (
        Number(
          this.sharedStorageService.get('businessSelectedPackage')?.price
        ) ?? 0
      );
    }
  }

  // 加選項目總金額
  get addExamItemPrice(): number {
    return (
      this.addExamItemList.filter((addExamInfo: AddExamInfo) => (addExamInfo.selectFlag ?? 1) === 1).reduce(
        (total, item) => total + (Number(item.discountedPrice) || 0),
        0
      ) ?? 0
    );
  }

  // 企業總金額
  get businessBillAmt(): number {
    if (!this.isPersonalReserve) {
      return this.paymentAmount + this.addExamItemPrice;
    }
    return 0;
  }

  // 企業補助金額
  get businessSponsorAmt(): number {
    const businessPlanResponse = this.sharedStorageService.get('businessPlanResponse');
    return Number(businessPlanResponse?.companyInfo?.sponsorAmt) || 0;
  }

  // 是否有企業補助金額
  get hasSponsorAmount(): boolean {
    return this.businessSponsorAmt > 0;
  }
  // 企業扣除補助後的自付金額
  get businessSelfAmt(): number {
    return this.businessBillAmt - this.businessSponsorAmt;
  }

  // 取得健檢時間類型
  get dayType(): string {
    return this.bookingInfo?.dayType === API_PARAMS.DAY_TYPE.HALF_DAY.code
      ? API_PARAMS.DAY_TYPE.HALF_DAY.name
      : API_PARAMS.DAY_TYPE.FULL_DAY.name;
  }

  // 健檢時間
  get healthCheckTime(): string {
    const bookingDate =
      this.sharedStorageService.get('bookingInfo')?.checkupDate;
    const time =
      Number(bookingDate?.bookTime) < 1200
        ? this.translateService.instant('appointment-confirm.morning')
        : this.translateService.instant('appointment-confirm.afternoon');
    return (
      this.twDatePipe.transform(bookingDate?.bookDate ?? '') +
      ' ' +
      time +
      bookingDate?.bookTime?.replace(/^(\d{2})(\d{2})$/, '$1:$2')
    );
  }

  // 營養諮詢時間
  get nutritionConsultationTime(): string | undefined {
    const nutBookingDate =
      this.sharedStorageService.get('bookingInfo')?.nutrition;

    if (nutBookingDate) {
      const time =
        Number(nutBookingDate?.bookTime) < 1200
          ? this.translateService.instant('appointment-confirm.morning')
          : this.translateService.instant('appointment-confirm.afternoon');
      return (
        this.twDatePipe.transform(nutBookingDate?.bookDate ?? '') +
        ' ' +
        time +
        nutBookingDate?.bookTime?.replace(/^(\d{2})(\d{2})$/, '$1:$2')
      );
    } else {
      this.isShowNutrition = false;//不顯示營養諮詢
      return '';
    }
  }

  // 健檢中心名稱
  get orgName(): string {
    const org = this.orgDetails.filter(
      orgDetail => orgDetail.orgNo === this.orgNo
    )[0];
    if (!org) return '';
    return `${org.orgName} (${org.orgAddress})`;
  }

  // 檢前資料地址
  get preExamAddress(): string {
    return (
      '' +
      this.bookingInfo?.city +
      this.bookingInfo?.district +
      this.bookingInfo?.address
    );
  }

  // 檢查報告領取方式
  get examReportAddress(): string {
    if (
      this.bookingInfo?.deliveryMethod ===
      API_PARAMS.DELIVERY_METHOD.DOMESTIC.code
    ) {
      // 國內
      // console.log("國內");
      return (
        API_PARAMS.DELIVERY_METHOD.DOMESTIC.name +
        "<span class='doc-divider'>・</span>" +
        this.bookingInfo?.reportCity +
        this.bookingInfo?.reportDistrict +
        this.bookingInfo?.reportAddress
      );
    } else if (
      this.bookingInfo?.deliveryMethod ===
      API_PARAMS.DELIVERY_METHOD.FOREIGN.code
    ) {
      // 國外
      // console.log("國外");
      return (
        API_PARAMS.DELIVERY_METHOD.FOREIGN.name +
        "<span class='doc-divider'>・</span>" +
        this.bookingInfo?.foreignAddress
      );
    }
    return '';
  }


  // 姓名
  get name(): string {
    const createMemberRequest = this.sharedStorageService.get(STORAGE_KEY.CREATE_MEMBER_REQUEST) as CreateMemberRequest | undefined;
    return this.userPlanRequest?.name || createMemberRequest?.name || '';
  }

  // 身份證字號
  get idNumber(): string {
    return this.userPlanRequest?.idNo ?? '';
  }

  // 生日
  get birthday(): string {
    return this.formatDatePipe.transform(this.userPlanRequest?.birthday ?? '');
  }

  // 報告寄送是否同資料袋寄送地址
  get isContactAddress(): boolean {
    //  2 承辦領取、3 統一給業務、99 其他或如果 reportSendType 為 9（郵寄國外），則不同資料袋寄送地址
    if (this.bookingInfo?.deliveryMethod === '9' || this.bookingInfo?.reportSendType === '99' || this.bookingInfo?.reportSendType === '2' || this.bookingInfo?.reportSendType === '3') {
      console.log("this.bookingInfo?.deliveryMethod:", this.bookingInfo?.deliveryMethod);
      console.log("this.bookingInfo?.reportSendType:", this.bookingInfo?.reportSendType);
      console.log("不同資料袋寄送地址，isContactAddress: false");
      return false;
    }

    // 比較檢前資料地址和報告地址
    const preExamAddress = '' +
      this.bookingInfo?.city +
      this.bookingInfo?.district +
      this.bookingInfo?.address;

    const deliveryAddress = '' +
      this.bookingInfo?.reportCity +
      this.bookingInfo?.reportDistrict +
      this.bookingInfo?.reportAddress;

    return preExamAddress === deliveryAddress;
  }

  // 高階影像檢查項目名稱列表
  get imageItemNames(): Array<string> {
    const imageExamItemNames =
      this.sharedStorageService
        .get('examItemList')
        ?.filter((item: ExamInfo) => {
          return item.deptName === IMAGE_DEPT_NAME && item.selectFlag;
        })
        .map(item => item.examName) ?? [];
    const imageAddExamItemNames =
      this.sharedStorageService
        .get('addExamItemList')
        ?.filter((item: AddExamInfo) => item.deptName === IMAGE_DEPT_NAME && item.selectFlag === 1)
        .map(item => item.examName) ?? [];
    return [...imageExamItemNames, ...imageAddExamItemNames];
  }

  // 開啟修改檢查相關資料與報告Dialog
  openModifyExamReportDialog() {
    const modifyExamReportDialogRef = this.dialog.open(
      ModifyExamReportDialogComponent,
      {
        panelClass: 'modify-dialog',
        autoFocus: false,
        data: {
          city: this.bookingInfo?.city ?? '',
          district: this.bookingInfo?.district ?? '',
          address: this.bookingInfo?.address ?? '',
          deliveryMethod:
            this.bookingInfo?.deliveryMethod ??
            API_PARAMS.DELIVERY_METHOD.DOMESTIC.code,
          noticeSendType: this.bookingInfo?.noticeSendType ?? '',
          reportCity: this.bookingInfo?.reportCity ?? '',
          reportDistrict: this.bookingInfo?.reportDistrict ?? '',
          reportAddress: this.bookingInfo?.reportAddress ?? '',
          deliveryRecipient: this.bookingInfo?.deliveryRecipient ?? '',
          recipient: this.bookingInfo?.recipient ?? '',
          recipientCell: this.bookingInfo?.recipientCell ?? '',
          foreignAddress: this.bookingInfo?.foreignAddress ?? '',
          email: this.bookingInfo?.email ?? '',
          language: this.bookingInfo?.language ?? '',
        },
      }
    );

    modifyExamReportDialogRef
      .afterClosed()
      .subscribe((result: ModifyExamReportDialogModel) => {
        if (!result) return; // 如果沒有結果，則不更新
        // 更新預約資訊
        this.bookingInfo = {
          ...this.bookingInfo!,
          zipCode: result.zipCode,
          city: result.city,
          district: result.district,
          address: result.address,
          deliveryMethod: result.deliveryMethod,
          reportZipCode: result.reportZipCode,
          reportCity: result.reportCity,
          reportDistrict: result.reportDistrict,
          reportAddress: result.reportAddress,
          deliveryRecipient: result.deliveryRecipient,
          recipient: result.recipient,
          recipientCell: result.recipientCell,
          foreignAddress: result.foreignAddress,
          email: result.email,
          language: result.language,
        };

        // 儲存更新後的預約資訊、報告語言
        this.sharedStorageService.set('bookingInfo', this.bookingInfo);

        //如果沒有收件人資訊就填本人的(已無非本人收件人)
        // const deliveryRecipient = this.bookingInfo?.deliveryRecipient ?? API_PARAMS.DELIVERY_METHOD.NOT_SELF.code;
        //   if (deliveryRecipient == API_PARAMS.DELIVERY_METHOD.SELF.code) {
        //     this.recipient = this.userPlanRequest?.name || this.createMemberRequest?.name || '';
        //     this.recipientCell = this.userPlanRequest?.cell || '0';
        //     this.isShowRecipientCell = false;
        //   } else {
        //     this.recipient = this.bookingInfo?.recipient ?? '';
        //     this.recipientCell = this.bookingInfo?.recipientCell ?? '';
        //     this.isShowRecipientCell = true;
        //   }
        console.log("this.recipient:", this.recipient)
        // 抓 API 語言(從代號找中文)
        const languageUno = this.bookingInfo?.language;
        this.examReportLanguage =
          this.languages.find(item => item.uno === languageUno)?.noDesc ?? '';
        modifyExamReportDialogRef.close();
      });
  }

  // 開啟修改健檢服尺寸Dialog
  openModifyExamClothesDialog() {
    // 建構 healthCheckPlan 格式的資料
    const examItemList = this.sharedStorageService.get('examItemList') ?? [];

    const healthCheckPlan = {
      bookData: {
        clothesSize: { uno: this.bookingInfo?.clothesSize ?? '' },
      },
      packageGroup: {
        packageInfo: {
          planList: [{
            examItemList: examItemList.map(item => ({
              examItem: item.examItem,
              examName: item.examName,
            }))
          }]
        }
      }
    } as any;

    this.dialogHelperService
      .openModifyClothesDialog(
        healthCheckPlan,
        this.isPersonalReserve,
        {}
      )
      .subscribe((result) => {
        if (!result) return;

        this.bookingInfo = {
          ...this.bookingInfo!,
          clothesSize: result.clothesSize,
        };

        this.sharedStorageService.set('bookingInfo', this.bookingInfo);

        // 抓 API 健檢服尺寸(從代號找中文)
        this.examClothesSize = result.clothesSizeNoDesc ?? '';
      });
  }

  // 開啟修改飲食Dialog
  openModifyDietDialog() {
    // 建構 healthCheckPlan 格式的資料
    const examItemList = this.sharedStorageService.get('examItemList') ?? [];

    const healthCheckPlan = {
      bookData: {
        breakfast: { uno: this.bookingInfo?.breakfast ?? '' },
        lunch: { uno: this.bookingInfo?.lunch ?? '' },
      },
      packageGroup: {
        packageInfo: {
          planList: [{
            examItemList: examItemList.map(item => ({
              examItem: item.examItem,
              examName: item.examName,
            }))
          }]
        }
      }
    } as any;

    this.dialogHelperService
      .openModifyDietDialog(
        healthCheckPlan,
        this.isPersonalReserve,
        {}
      )
      .subscribe((result) => {
        if (!result) return;

        this.bookingInfo = {
          ...this.bookingInfo!,
          breakfast: result.breakfast ?? '',
          lunch: result.lunch ?? '',
        };

        this.sharedStorageService.set('bookingInfo', this.bookingInfo);

        this.breakfast = result.breakfastDesc ?? '';
        this.lunch = result.lunchDesc ?? '';
      });
  }

  // 顯示檢項dialog
  showExamDialog(examInfo: ExamInfo | AddExamInfo | BusinessExamInfo) {
    const examDialog = this.dialog.open(SingleActionDialogComponent, {
      data: {
        title: examInfo.examName,
        message: examInfo.examMemo,
        buttonText: this.translateService.instant('common.button.i-understood'),
        showCloseButton: false,
        action: () => examDialog.close(),
      },
    });
  }

  // 顯示組合檢項dialog
  showGroupDialog(group: Group) {
    const examDialog = this.dialog.open(SingleActionDialogComponent, {
      data: {
        title: group.groupName,
        message: group.groupMemo,
        buttonText: this.translateService.instant('common.button.i-understood'),
        showCloseButton: false,
        action: () => examDialog.close(),
      },
    });
  }

  // 點擊預約資料的編輯，前往選擇預約時間頁面
  previous() {
    this.router.navigate(['/healthcheck/reserve/timeselect']).then();
  }

  // 點擊"確認預約"
  confirm() {
    this.isAppointmentSent = true;
    if (this.isPersonalReserve) {
      // 個人預約

      const examItemList = this.sharedStorageService.get('examItemList') ?? [];
      this.planRemoveExamList = examItemList.filter(item => item.selectFlag === 0).map(item => item.examItem);

      this.uuid = this.userPlanRequest?.uuid ?? '';
      console.log('走快速預約，this.uuid:', this.uuid);

      const addExamPrice = (this.addExamItemList ?? []).reduce((total, item) => {
        return total.add(new Decimal(item.discountedPrice ?? item.price ?? '0'));
      }, new Decimal(0));

      // 改成扣除加選項目的價格
      const price = new Decimal(this.paymentAmount).sub(addExamPrice);


      //deliveryAddress判斷
      let deliveryAddress = '';
      if (this.bookingInfo?.deliveryMethod ===
        API_PARAMS.DELIVERY_METHOD.DOMESTIC.code &&
        !this.isContactAddress) {
        deliveryAddress = '' +
          this.bookingInfo?.reportCity +
          this.bookingInfo?.reportDistrict +
          this.bookingInfo?.reportAddress;
      } else if (this.bookingInfo?.deliveryMethod ===
        API_PARAMS.DELIVERY_METHOD.FOREIGN.code) {
        deliveryAddress = this.bookingInfo?.foreignAddress ?? '';
      }

      const cell = this.userPlanRequest?.cell || this.createMemberRequest?.cell;
      const request: ConfirmAppointmentRequest = {
        UUID: this.uuid,
        empId: '',
        isFront: '1',
        personalData: {
          idNo: this.userPlanRequest?.idNo ?? '',
          name: this.userPlanRequest?.name || this.createMemberRequest?.name || '',
          birthday: this.userPlanRequest?.birthday ?? '',
          gender: this.userPlanRequest?.gender ?? '',
          cell: cell || '0',
          noCellReason: (!cell || cell === '0') ? '3' : '',
          email: this.userPlanRequest?.email || this.createMemberRequest?.email || '',
          address: this.preExamAddress,
          telH: this.userPlanRequest?.telH || '0',
          telO: this.userPlanRequest?.telO || '0',
          zipCode: this.bookingInfo?.zipCode ?? '',
          nationality: API_PARAMS.NATIONALITY.TW,
          nationalityOther: '',
          bloodType: '',
          langu: this.bookingInfo?.language ?? '',
          isPregnant: this.userPlanRequest?.femaleStatus ? 'Y' : 'N',
        },
        examItemData: {
          planType: this.sharedStorageService.get('selectedPackagePlanType') ?? '',
          planRemoveExamList: this.planRemoveExamList,
          addExamList: this.addExamItemList.map(item => item.examItem),
        },
        bookData: {
          orgNo: this.orgNo,
          price: price,
          bookDate: this.bookingInfo?.checkupDate.bookDate ?? '',
          bookTime: this.bookingInfo?.checkupDate.bookTime ?? '',
          nutBookDate: this.bookingInfo?.nutrition?.bookDate ?? '',
          nutBookTime: this.bookingInfo?.nutrition?.bookTime?.replace(':', '') ?? '',
          imagingContact: this.bookingInfo?.imagingContact,
          deliveryType: this.bookingInfo?.deliveryMethod || '0',
          isContactAddress: this.isContactAddress,
          zipCode: this.bookingInfo?.deliveryMethod == API_PARAMS.DELIVERY_METHOD.DOMESTIC.code
            ? this.bookingInfo?.reportZipCode ?? '' : '',
          deliveryAddress: deliveryAddress,
          recipient: this.recipient ?? '',
          recipientCell: this.recipientCell || '0',
          clothesSize: this.bookingInfo?.clothesSize ?? '',
          breakfast: this.bookingInfo?.breakfast ?? '',
          lunch: this.bookingInfo?.lunch ?? '',
        },
      };
      console.log('CB-056 request:', request);
      this.appointmentConfirmService
        .confirmAppointment(request)
        .subscribe(res => {
          if (res.success && res.statusCode === 'S0000') {
            const caseNo = res.data?.caseNo ?? '';
            const chartNo = res.data?.chartNo ?? '';
            //提供馬上去會員中心查看時CB-007用
            this.sharedStorageService.set('caseNo', caseNo);
            if (this.createMemberRequest) {
              this.createMemberRequest.chartNo = chartNo;
              this.sharedStorageService.set('createMemberRequest', this.createMemberRequest);
            }
            //清除預約相關的暫存資料
            this.sharedStorageService.remove('addExamItemList');
            this.sharedStorageService.remove('bookingInfo');
            this.sharedStorageService.remove('examItemList');
            this.sharedStorageService.remove('packagePrice');
            this.sharedStorageService.remove('reserveType');
            this.sharedStorageService.remove('selectedPackage');
            this.sharedStorageService.remove('selectedPackagePlanType');
            this.sharedStorageService.remove('userPlanRequest');
            this.sharedStorageService.remove('userPlanResponse');

            this.router.navigate(['/healthcheck/reserve/complete']).then();
          } else {
            // 失敗處理（顯示錯誤訊息）
            if (res.statusCode === 'EL1001') {
              //已有預約資料
              const examDialog = this.dialog.open(SingleActionDialogComponent, {
                data: {
                  title: this.translateService.instant(
                    'my-reservation.completed-exam-title'
                  ),
                  message: this.translateService.instant(
                    'my-reservation.completed-exam-subtitle'
                  ),
                  buttonText: this.translateService.instant(
                    'common.button.confirm'
                  ),
                  showCloseButton: false,
                  action: () => examDialog.close(),
                },
              });

              this.router.navigate(['/healthcheck/member/mybooking']).then();
            }
            else if (res.statusCode === 'EL1000' || res.statusCode === 'EL1001' || res.statusCode === 'EL1002') {
              this.toastr.error(
                this.translateService.instant('appointment-confirm.reservation-failed-hint')
              );
              //留在原頁
            }

            else if (res.statusCode === 'EL1003' || res.statusCode === 'EL1004' || res.statusCode === 'EL1005') {
              const rebookDialog = this.dialog.open(SingleActionDialogComponent, {
                data: {
                  title: this.translateService.instant(
                    'appointment-confirm.please-rebook-hint'
                  ),
                  buttonText: this.translateService.instant(
                    'my-reservation.modify-specific-item.button'
                  ),
                  showCloseButton: false,
                  action: () => {
                    rebookDialog.close();
                    this.sharedStorageService.clear();
                    //導回首頁
                    this.router.navigate(['/healthcheck']).then();
                  },
                },
              });

            }

            else if (res.statusCode === 'EL1006' || res.statusCode === 'EL1008') {
              const quotaFilledDialog = this.dialog.open(SingleActionDialogComponent, {
                data: {
                  title: this.translateService.instant(
                    'appointment-time.quota-filled'
                  ),
                  buttonText: this.translateService.instant(
                    'my-reservation.modify-specific-item.button'
                  ),
                  showCloseButton: false,
                  action: () => {
                    quotaFilledDialog.close();
                    //引導重選時間
                    this.router.navigate(['/healthcheck/reserve/timeselect']).then();
                  },
                },
              });

            }

            else {
              //其他錯誤
              const rebookDialog = this.dialog.open(SingleActionDialogComponent, {
                data: {
                  title: this.translateService.instant(
                    'appointment-confirm.please-rebook-hint'
                  ),
                  message: res.errorMessage,
                  buttonText: this.translateService.instant(
                    'my-reservation.modify-specific-item.button'
                  ),
                  showCloseButton: false,
                  action: () => {
                    rebookDialog.close();
                    this.sharedStorageService.clear();
                    //導回首頁
                    this.router.navigate(['/healthcheck']).then();
                  },
                },
              });
            }

          }
        });
    } else {
      // 企業預約

      //deliveryAddress判斷
      let deliveryAddress = '';
      if (this.bookingInfo?.deliveryMethod ===
        API_PARAMS.DELIVERY_METHOD.DOMESTIC.code) {
        deliveryAddress = '' +
          this.bookingInfo?.reportCity +
          this.bookingInfo?.reportDistrict +
          this.bookingInfo?.reportAddress;
      } else if (this.bookingInfo?.deliveryMethod ===
        API_PARAMS.DELIVERY_METHOD.FOREIGN.code) {
        deliveryAddress = this.bookingInfo?.foreignAddress ?? '';
      }

      const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');
      const businessPlanRequest = this.sharedStorageService.get('businessPlanRequest');
      const businessPlanResponse = this.sharedStorageService.get('businessPlanResponse');
      const idLoginRequest = this.sharedStorageService.get('idLoginRequest');

      let businessDeliveryType = '';
      if (this.bookingInfo?.reportSendType === '1') {
        businessDeliveryType = this.bookingInfo?.deliveryMethod;
      }
      //2025.12.8若 reportSendType 為 2 - 承辦領取、3 - 統一給業務、99 - 其他，則不需填寫若為承辦領取 or 不需寄送，不需填寫

      const cell = this.userPlanRequest?.cell || this.createMemberRequest?.cell;
      // 取得企業已選檢項
      const request: BusinessConfirmAppointmentRequest = {
        personalData: {
          idNo: this.createMemberRequest?.idNo ?? idLoginRequest?.idNo ?? '',
          name: this.createMemberRequest?.name ?? idLoginRequest?.name ?? '',
          birthday: this.createMemberRequest?.birthday ?? idLoginRequest?.birthday ?? '',
          gender: this.createMemberRequest?.gender ?? businessPlanRequest?.gender ?? '',
          cell: cell || '0',
          noCellReason: (!cell || cell === '0') ? '3' : '',
          email: this.createMemberRequest?.email ?? '',
          address: this.preExamAddress,
          telH: this.userPlanRequest?.telH || '0',
          telO: this.userPlanRequest?.telO || '0',
          zipCode: this.bookingInfo?.zipCode ?? '',
          nationality: API_PARAMS.NATIONALITY.TW,
          nationalityOther: '',
          bloodType: '',
          langu: this.bookingInfo?.language ?? '1',
          isPregnant: 'N',//2025.11.19現行方案若懷孕或疑似懷孕就無法預約
        },
        examItemData: {
          examItemList: this.sharedStorageService.get('examItemList')?.map(item => ({
            examItem: item.examItem,
            examName: item.examName,
          })) ?? [],
          addList: this.sharedStorageService
            .get('addExamItemList')
            ?.filter((addExamInfo: AddExamInfo) => (addExamInfo.selectFlag ?? 0) === 1)
            ?.map(addExamItem => ({
              examItem: addExamItem.examItem,
              examName: addExamItem.examName,
              examPrice: addExamItem.discountedPrice ?? '0',
            })) ?? [],
        },
        bookData: {
          orgNo: this.orgNo,
          companyNo: businessPlanRequest?.companyNo ?? '',
          employeeId: businessPlanRequest?.employeeId ?? '',
          familyFlag: idLoginRequest?.identity ?? API_PARAMS.IDENTITY.EMPLOYEE,
          packageNo: businessSelectedPackage?.packageNo ?? '',
          price:
            this.sharedStorageService.get('paymentAmount')?.toString() ?? '0',
          sponsorAmt: businessPlanResponse?.companyInfo?.sponsorAmt ?? '0',
          bookDate: this.bookingInfo?.checkupDate.bookDate ?? '',
          bookTime: this.bookingInfo?.checkupDate.bookTime ?? '',
          nutBookDate: this.bookingInfo?.nutrition?.bookDate ?? '',
          nutBookTime: this.bookingInfo?.nutrition?.bookTime?.replace(':', '') ?? '',
          imagingContact: this.bookingInfo?.imagingContact ?? '',
          deliveryType: businessDeliveryType,
          isContactAddress: this.isContactAddress,
          zipCode: this.bookingInfo?.deliveryMethod == API_PARAMS.DELIVERY_METHOD.DOMESTIC.code || this.bookingInfo?.deliveryMethod == API_PARAMS.DELIVERY_METHOD.INDIVIDUAL.code
            ? this.bookingInfo?.reportZipCode ?? '' : '',
          deliveryAddress: deliveryAddress,
          recipient: this.recipient ?? idLoginRequest?.name ?? this.createMemberRequest?.name ?? '',
          recipientCell: this.recipientCell ?? this.createMemberRequest?.cell ?? '0',
          clothesSize: this.bookingInfo?.clothesSize ?? '',
          breakfast: this.bookingInfo?.breakfast ?? '',
          lunch: this.bookingInfo?.lunch ?? '',
        },
        empId: '',
        isFront: '1',

      };
      console.log('CB-065 request:', request);
      this.appointmentConfirmService
        .businessConfirmAppointment(request)
        .subscribe(res => {
          if (res.success && res.statusCode === 'S0000') {
            console.log('企業預約成功，res.data:', res.data);
            const caseNo = res.data?.caseNo ?? '';
            const chartNo = res.data?.chartNo ?? '';
            //提供馬上去會員中心查看時CB-007用
            this.sharedStorageService.set('caseNo', caseNo);
            if (this.createMemberRequest) {
              this.createMemberRequest.chartNo = chartNo;
              this.sharedStorageService.set('createMemberRequest', this.createMemberRequest);
            }
            this.router.navigate(['/healthcheck/reserve/complete']).then();

          } else {
            // 待確認
            // 失敗處理（顯示錯誤訊息）
            if (res.statusCode === 'EL1001') {
              //已有預約資料
              const examDialog = this.dialog.open(SingleActionDialogComponent, {
                data: {
                  title: this.translateService.instant(
                    'my-reservation.completed-exam-title'
                  ),
                  message: this.translateService.instant(
                    'my-reservation.completed-exam-subtitle'
                  ),
                  buttonText: this.translateService.instant(
                    'common.button.confirm'
                  ),
                  showCloseButton: false,
                  action: () => examDialog.close(),
                },
              });

              this.router.navigate(['/healthcheck/member/mybooking']).then();
            }
            else if (res.statusCode === 'EL1000' || res.statusCode === 'EL1001' || res.statusCode === 'EL1002') {
              this.toastr.error(
                this.translateService.instant('appointment-confirm.reservation-failed-hint')
              );
              //留在原頁
            }

            else if (res.statusCode === 'EL1003' || res.statusCode === 'EL1004' || res.statusCode === 'EL1005') {
              const rebookDialog = this.dialog.open(SingleActionDialogComponent, {
                data: {
                  title: this.translateService.instant(
                    'appointment-confirm.please-rebook-hint'
                  ),
                  buttonText: this.translateService.instant(
                    'my-reservation.modify-specific-item.button'
                  ),
                  showCloseButton: false,
                  action: () => {
                    rebookDialog.close();
                    this.sharedStorageService.clear();
                    //導回首頁
                    this.router.navigate(['/healthcheck']).then();
                  },
                },
              });

            }

            else if (res.statusCode === 'EL1006' || res.statusCode === 'EL1008') {
              const quotaFilledDialog = this.dialog.open(SingleActionDialogComponent, {
                data: {
                  title: this.translateService.instant(
                    'appointment-time.quota-filled'
                  ),
                  buttonText: this.translateService.instant(
                    'my-reservation.modify-specific-item.button'
                  ),
                  showCloseButton: false,
                  action: () => {
                    quotaFilledDialog.close();
                    //引導重選時間
                    this.router.navigate(['/healthcheck/reserve/timeselect']).then();
                  },
                },
              });

            }

            else {
              //其他錯誤
              const rebookDialog = this.dialog.open(SingleActionDialogComponent, {
                data: {
                  title: this.translateService.instant(
                    'appointment-confirm.please-rebook-hint'
                  ),
                  message: res.errorMessage,
                  buttonText: this.translateService.instant(
                    'my-reservation.modify-specific-item.button'
                  ),
                  showCloseButton: false,
                  action: () => {
                    rebookDialog.close();
                    this.sharedStorageService.clear();
                    //導回首頁
                    this.router.navigate(['/healthcheck']).then();
                  },
                },
              });
            }

          }

        });
    }
  }

  /**
   * 計算動態折扣率
   */
  movedDiscountRate(originalPrice: Decimal): Decimal {
    console.log("計算折扣率 - originalPrice:", originalPrice.toString());

    // 防護：如果 originalPrice 為負數或零，返回基礎折扣率
    if (originalPrice.lte(0)) {
      console.warn("originalPrice 異常:", originalPrice.toString());
      return new Decimal(1);
    }

    const userPlanResponse = this.sharedStorageService.get('userPlanResponse');

    //取得原始售價(未折扣)
    let allcheckupPrice = new Decimal(userPlanResponse?.allCheckup?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0);
    let aiExclusivePrice = new Decimal(userPlanResponse?.aiExclusive?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0);
    const basicCheckupPrice = new Decimal(userPlanResponse?.basicCheckup?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0);

    // 取得原始折扣率
    const allCheckupRate = new Decimal(userPlanResponse?.allCheckup?.discountRate ?? 0);
    const aiExclusiveRate = new Decimal(userPlanResponse?.aiExclusive?.discountRate ?? 0);
    const basicCheckupRate = new Decimal(userPlanResponse?.basicCheckup?.discountRate ?? 0);

    // 取得三寶金額
    const threeBaoPrice = this.threeBaoItems.reduce((total, item) => {
      return total.add(new Decimal(item.examPrice ?? 0));
    }, new Decimal(0));

    // 檢查是否選取三寶
    const hasThreeBaoSelected = this.sharedStorageService.get('examItemList')?.some(item =>
      this.threeBaoItems.some(tb => tb.examItem === item.examItem && item.selectFlag === 1)
    );

    if (hasThreeBaoSelected) {
      // 先扣除三寶金額，避免三寶影響折扣率計算
      originalPrice = originalPrice.sub(threeBaoPrice);
    }

    aiExclusivePrice = aiExclusivePrice.sub(threeBaoPrice);
    allcheckupPrice = allcheckupPrice.sub(threeBaoPrice);

    // 當目前原價大於等於全方位方案價格時
    if (originalPrice.gte(allcheckupPrice)) {
      console.log("使用全方位折扣率");
      return allCheckupRate;
    }
    // 當目前原價大於等於智能專屬方案價格時
    else if (originalPrice.gte(aiExclusivePrice)) {
      if (originalPrice.eq(aiExclusivePrice)) {
        console.log("使用智能專屬折扣率");
        return aiExclusiveRate;
      }
      console.log("計算全方位與智能專屬之間的折扣率");
      return allCheckupRate.sub(
        (allCheckupRate.sub(aiExclusiveRate))
          .mul((allcheckupPrice.sub(originalPrice)))
          .div((allcheckupPrice.sub(aiExclusivePrice)))
      );
    }
    // 當目前原價大於等於基礎方案價格時
    else if (originalPrice.gte(basicCheckupPrice)) {
      if (originalPrice.eq(basicCheckupPrice)) {
        console.log("使用基礎折扣率");
        return basicCheckupRate;
      }
      console.log("計算智能專屬與基礎之間的折扣率");
      return aiExclusiveRate.sub(
        ((aiExclusiveRate.sub(basicCheckupRate))).mul((aiExclusivePrice.sub(originalPrice)))
          .div((aiExclusivePrice.sub(basicCheckupPrice)))
      );
    }
    // 當目前原價小於基礎方案價格時
    else {
      console.log("使用基礎折扣率");
      return new Decimal(basicCheckupRate);
    }
  }

  // 取得個別examType底下deptName是否展開
  getDeptOpenStateByExamType(examType: string, deptName: string) {
    return this.isDeptNameOpenByExamType.get(examType)?.get(deptName) ?? false;
  }

  // 切換個別examType底下deptName的展開狀態
  toggleDeptOpenStateByExamType(examType: string, deptName: string) {
    if (!this.isDeptNameOpenByExamType.has(examType)) {
      this.isDeptNameOpenByExamType.set(examType, new Map<string, boolean>());
    }
    const deptMap = this.isDeptNameOpenByExamType.get(examType)!;
    deptMap.set(deptName, !(deptMap.get(deptName) ?? false));
  }

  // 移除科別名稱中的英文部分
  removeDeptNameEnglish(deptName: string): string {
    if (!deptName) return deptName;
    // 移除括號及其內容（包含英文）
    return deptName.replace(/\([^)]*\)/g, '').trim();
  }

  protected readonly FULL_DAY_CHECKUP_HOURS = FULL_DAY_CHECKUP_HOURS;
  protected readonly API_PARAMS = API_PARAMS;
}
